'use client'

/**
 * Today's tasks, in two tiers.
 *
 * A pure selector over the resolved view, plus the thin hook that reads it — ADR 0012's shape for a
 * read. `useDocumentSelector` does not exist yet (`document/use-document.ts:7`), so the hook calls
 * `useDocumentView` itself and collapses to one line when that lands.
 */

import { useMemo } from 'react'

import { useDocumentView, type DocumentView, type Entry } from '@/document'
import { formatDateString } from '@/lib/date'
import type { TaskMeta } from './meta'
import { isDueTodayCandidate } from './predicates'

export interface TodayTasksResult {
  /** Due before today, most overdue first. */
  overdueTasks: Entry[]
  /** Due exactly today: important first, then oldest first. */
  dueTodayTasks: Entry[]
}

const EMPTY_RESULT: TodayTasksResult = { overdueTasks: [], dueTodayTasks: [] }

function dueDateOf(entry: Entry): string {
  return (entry.metadata as TaskMeta).dueDate ?? ''
}

/**
 * Split the view's live, dated, non-`someday` tasks into overdue and due-today.
 *
 * Tasks with no due date are excluded — they belong to Upcoming or Projects. That, and the
 * `someday` exclusion, is `isDueTodayCandidate`; the two other surfaces answer differently, and
 * `predicates.ts` sets the three side by side.
 */
export function selectTodayTasks(
  view: DocumentView | null,
  today: Date = new Date(),
): TodayTasksResult {
  if (!view) return EMPTY_RESULT

  const todayStr = formatDateString(today)
  const overdue: Entry[] = []
  const dueToday: Entry[] = []

  for (const entry of Object.values(view.entries)) {
    if (!isDueTodayCandidate(entry)) continue

    const dueDate = dueDateOf(entry)
    if (dueDate < todayStr) {
      overdue.push(entry)
    } else if (dueDate === todayStr) {
      dueToday.push(entry)
    }
  }

  // Most overdue first: the earliest date is the most pressing.
  overdue.sort((a, b) => dueDateOf(a).localeCompare(dueDateOf(b)))

  // Important first, then oldest first. `priority === 'high'` is the legacy spelling of important,
  // and it is what this list has always sorted on.
  dueToday.sort((a, b) => {
    const aImportant = (a.metadata as TaskMeta).priority === 'high' ? 1 : 0
    const bImportant = (b.metadata as TaskMeta).priority === 'high' ? 1 : 0
    if (aImportant !== bImportant) return bImportant - aImportant
    return a.createdAt.localeCompare(b.createdAt)
  })

  return { overdueTasks: overdue, dueTodayTasks: dueToday }
}

export function useTodayTasks(): TodayTasksResult {
  const view = useDocumentView()
  return useMemo(() => selectTodayTasks(view), [view])
}
